import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiCornerUpRight, FiX } from 'react-icons/fi';
import chatService from '../../../services/chatService';
import Avatar from '../Shared/Avatar';

export default function ForwardMessageModal({ message, sourceId, currentUserId, conversations,
  hasMore, loadingMore, onLoadMore, onClose, onForwarded }) {
  const [destination, setDestination] = useState(null);
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState([]);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const panel = useRef(null);
  const callbacks = useRef({ onClose, onForwarded });
  callbacks.current = { onClose, onForwarded };

  useEffect(() => {
    mounted.current = true;
    const previousFocus = document.activeElement;
    panel.current?.querySelector('button')?.focus();
    const keydown = event => {
      if (event.key === 'Escape' && !inFlight.current) callbacks.current.onClose();
      if (event.key === 'Tab') {
        const controls = Array.from(panel.current?.querySelectorAll('button:not(:disabled), input:not(:disabled)') || []);
        const first = controls[0], last = controls[controls.length - 1];
        if ((event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
          event.preventDefault();
          (event.shiftKey ? last : first)?.focus();
        }
      }
    };
    document.addEventListener('keydown', keydown);
    return () => {
      mounted.current = false;
      document.removeEventListener('keydown', keydown);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  const submit = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSending(true);
    setErrors([]);
    try {
      const result = await chatService.forwardMessage(message.id, destination);
      if (!mounted.current) return;
      if (!result.status) {
        setErrors([result.message || 'Could not forward this message. Please try again.']);
        return;
      }
      callbacks.current.onForwarded(result.data.message);
      callbacks.current.onClose();
    } catch (error) {
      if (mounted.current) setErrors(error.status === 422
        ? Object.values(error.errors || {}).flat()
        : ['Could not forward this message. Please try again.']);
    } finally {
      inFlight.current = false;
      if (mounted.current) setSending(false);
    }
  };

  return createPortal(
    <div className="talk-forward-layer" onClick={event => {
      if (event.target === event.currentTarget && !inFlight.current) onClose();
    }}>
      <div className="talk-forward-panel talk-forward-dialog" ref={panel} role="dialog" aria-modal="true" aria-labelledby="talk-forward-title" aria-busy={sending}>
        <div className="talk-forward-heading">
          <h2 id="talk-forward-title"><FiCornerUpRight aria-hidden="true" /> Forward message</h2>
          <button type="button" className="talk-forward-close" aria-label="Close forwarding" disabled={sending} onClick={onClose}><FiX aria-hidden="true" /></button>
        </div>
        <p className="talk-forward-preview">{message.body || message.uploads?.map(upload => upload.name).join(', ') || 'Attachment'}</p>
        <div className="talk-forward-destinations" role="radiogroup" aria-label="Destination chat">
          {conversations.filter(chat => chat.id !== sourceId).map(chat => {
            const name = chat.type === 'direct'
              ? chat.users?.find(user => user.id !== currentUserId)?.name || 'Chat'
              : chat.name || chat.title || chat.context?.name ||
                chat.users?.filter(user => user.id !== currentUserId).map(user => user.name).join(', ') || 'Group chat';
            return <label className={`talk-forward-destination${destination === chat.id ? ' talk-forward-destination-selected' : ''}`} key={chat.id}>
              <input type="radio" name="forward-destination" value={chat.id} checked={destination === chat.id}
                disabled={sending} onChange={() => setDestination(chat.id)} />
              <span className="talk-forward-destination-avatar" aria-hidden="true"><Avatar name={name} size={36} /></span>
              <span>{name}</span>
            </label>;
          })}
          {!conversations.some(chat => chat.id !== sourceId) && <p>No other chats available.</p>}
          {hasMore && <button type="button" className="talk-forward-load-more" disabled={sending || loadingMore} onClick={onLoadMore}>
            {loadingMore ? 'Loading…' : 'Load more chats'}
          </button>}
        </div>
        {errors.length > 0 && <div role="alert" className="talk-action-error">{errors.map((error, index) => <p key={index}>{error}</p>)}</div>}
        <div className="talk-forward-footer">
          <button type="button" className="talk-forward-cancel" disabled={sending} onClick={onClose}>Cancel</button>
          <button type="button" className="talk-forward-submit" disabled={sending || destination === null} onClick={submit}>
            <FiCornerUpRight aria-hidden="true" /> {sending ? 'Forwarding…' : 'Forward'}
          </button>
        </div>
      </div>
    </div>, document.body
  );
}
